/**
 * Bottom Sheet Component
 * Google Maps-style bottom sheet with gesture support using Hammer.js
 */

import Hammer from "hammerjs";
import { addClass, removeClass, hasClass, toggleClass } from "../utils/dom-helpers.js";

// Constants for bottom sheet states
const STATES = {
  COLLAPSED: "collapsed",
  EXPANDED: "expanded",
  FULL: "full",
};

// State class mappings
const STATE_CLASSES = {
  [STATES.COLLAPSED]: "bs--collapsed",
  [STATES.EXPANDED]: "bs--expanded",
  [STATES.FULL]: "bs--full",
};

// Configuration defaults
const DEFAULT_CONFIG = {
  enableGestures: true,
  enableKeyboard: true,
  initialState: STATES.COLLAPSED,
  velocityThreshold: 0.3,
  distanceThreshold: 50,
  animationDuration: 300,
  debug: false,
};

export class BottomSheet {
  constructor(container, config = {}) {
    if (!container) {
      throw new Error("BottomSheet: Container element is required");
    }

    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.currentState = this.config.initialState;
    this.isTransitioning = false;
    this.startY = 0;
    this.currentY = 0;
    this.isDragging = false;

    // Element references
    this.handle = null;
    this.content = null;
    this.body = null;

    // Hammer.js instance
    this.hammer = null;

    // Viewport dimensions
    this.viewportHeight = window.innerHeight;
    this.headerHeight = 0;

    // Event listeners array for cleanup
    this.eventListeners = [];

    this.init();
  }

  /**
   * Initialize the bottom sheet component
   */
  init() {
    try {
      this.setupElements();
      this.calculateDimensions();
      this.setupGestures();
      this.setupKeyboard();
      this.setupEventListeners();
      this.setState(this.currentState, false);

      this.log("Bottom sheet initialized successfully");
    } catch (error) {
      console.error("BottomSheet initialization error:", error);
    }
  }

  /**
   * Setup element references and validate structure
   */
  setupElements() {
    this.handle = this.container.querySelector("[data-bs-handle]");
    this.content = this.container.querySelector("[data-bs-content]");
    this.body = this.container.querySelector("[data-bs-body]");

    if (!this.handle || !this.content) {
      throw new Error("BottomSheet: Required elements not found (handle, content)");
    }

    // Add initial classes
    addClass(this.container, "bottomsheet");

    // Setup action buttons
    this.setupActionButtons();
  }

  /**
   * Setup action buttons event listeners
   */
  setupActionButtons() {
    const actionButtons = this.container.querySelectorAll("[data-bs-action]");

    actionButtons.forEach((button) => {
      const action = button.getAttribute("data-bs-action");
      const listener = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleAction(action);
      };

      button.addEventListener("click", listener);
      this.eventListeners.push({ element: button, event: "click", listener });
    });
  }

  /**
   * Handle action button clicks
   */
  handleAction(action) {
    switch (action) {
      case "minimize":
        this.setState(STATES.COLLAPSED);
        break;
      case "expand":
        this.setState(STATES.EXPANDED);
        break;
      case "close":
        this.setState(STATES.COLLAPSED);
        break;
      default:
        this.log(`Unknown action: ${action}`);
    }
  }

  /**
   * Calculate viewport and element dimensions
   */
  calculateDimensions() {
    this.viewportHeight = window.innerHeight;

    // Get header height from CSS variable or calculate
    const headerEl = document.querySelector("header, .header, [data-header]");
    this.headerHeight = headerEl ? headerEl.offsetHeight : 0;

    // Update CSS variables
    document.documentElement.style.setProperty("--header-height", `${this.headerHeight}px`);
    document.documentElement.style.setProperty("--viewport-height", `${this.viewportHeight}px`);
  }

  /**
   * Setup Hammer.js gesture recognition
   */
  setupGestures() {
    if (!this.config.enableGestures || !this.handle) return;

    try {
      this.hammer = new Hammer(this.handle);

      // Configure recognizers
      this.hammer.get("pan").set({
        direction: Hammer.DIRECTION_VERTICAL,
        threshold: 10,
      });

      this.hammer.get("swipe").set({
        direction: Hammer.DIRECTION_VERTICAL,
        threshold: 50,
        velocity: 0.3,
      });

      // Gesture event listeners
      this.hammer.on("panstart", this.handlePanStart.bind(this));
      this.hammer.on("panmove", this.handlePanMove.bind(this));
      this.hammer.on("panend", this.handlePanEnd.bind(this));
      this.hammer.on("swipeup", this.handleSwipeUp.bind(this));
      this.hammer.on("swipedown", this.handleSwipeDown.bind(this));
      this.hammer.on("tap", this.handleTap.bind(this));

      this.log("Gestures initialized successfully");
    } catch (error) {
      console.warn("BottomSheet: Failed to initialize gestures:", error);
    }
  }

  /**
   * Setup keyboard event listeners
   */
  setupKeyboard() {
    if (!this.config.enableKeyboard) return;

    const keydownListener = (e) => {
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          this.setState(STATES.COLLAPSED);
          break;
        case "ArrowUp":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.expandState();
          }
          break;
        case "ArrowDown":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.collapseState();
          }
          break;
      }
    };

    document.addEventListener("keydown", keydownListener);
    this.eventListeners.push({
      element: document,
      event: "keydown",
      listener: keydownListener,
    });
  }

  /**
   * Setup additional event listeners
   */
  setupEventListeners() {
    // Window resize
    const resizeListener = () => {
      this.calculateDimensions();
      this.setState(this.currentState, false);
    };

    window.addEventListener("resize", resizeListener);
    this.eventListeners.push({
      element: window,
      event: "resize",
      listener: resizeListener,
    });

    // Handle focus management
    const focusListener = (e) => {
      if (this.currentState === STATES.FULL && !this.container.contains(e.target)) {
        // Optionally return focus to bottom sheet when it's full screen
      }
    };

    document.addEventListener("focusin", focusListener);
    this.eventListeners.push({
      element: document,
      event: "focusin",
      listener: focusListener,
    });
  }

  /**
   * Handle pan start gesture
   */
  handlePanStart(e) {
    this.isDragging = true;
    this.startY = e.center.y;
    this.currentY = e.center.y;

    addClass(this.container, "bs--no-transition");
    this.log("Pan start", { startY: this.startY });
  }

  /**
   * Handle pan move gesture
   */
  handlePanMove(e) {
    if (!this.isDragging) return;

    this.currentY = e.center.y;
    const deltaY = this.currentY - this.startY;

    // Apply transform based on current state and delta
    this.applyTransform(deltaY);
    this.log("Pan move", { deltaY, currentY: this.currentY });
  }

  /**
   * Handle pan end gesture
   */
  handlePanEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    removeClass(this.container, "bs--no-transition");

    const deltaY = this.currentY - this.startY;
    const velocity = e.velocityY;

    this.determineNewState(deltaY, velocity);
    this.log("Pan end", { deltaY, velocity });
  }

  /**
   * Handle swipe up gesture
   */
  handleSwipeUp(e) {
    if (e.velocity >= this.config.velocityThreshold) {
      this.expandState();
      this.log("Swipe up detected");
    }
  }

  /**
   * Handle swipe down gesture
   */
  handleSwipeDown(e) {
    if (e.velocity >= this.config.velocityThreshold) {
      this.collapseState();
      this.log("Swipe down detected");
    }
  }

  /**
   * Handle tap gesture (toggle state)
   */
  handleTap(e) {
    if (e.tapCount === 1) {
      this.toggleState();
      this.log("Tap detected - toggling state");
    }
  }

  /**
   * Apply transform during drag
   */
  applyTransform(deltaY) {
    const maxDeltaY = this.viewportHeight * 0.8;
    const constrainedDelta = Math.max(-maxDeltaY, Math.min(maxDeltaY, deltaY));

    let baseTransform;
    switch (this.currentState) {
      case STATES.COLLAPSED:
        baseTransform = `calc(100% - var(--bottomsheet-collapsed-height))`;
        break;
      case STATES.EXPANDED:
        baseTransform = `calc(100% - var(--bottomsheet-expanded-height))`;
        break;
      case STATES.FULL:
        baseTransform = "0";
        break;
      default:
        baseTransform = "100%";
    }

    this.container.style.transform = `translateY(${baseTransform}) translateY(${constrainedDelta}px)`;
  }

  /**
   * Determine new state based on drag distance and velocity
   */
  determineNewState(deltaY, velocity) {
    const threshold = this.config.distanceThreshold;
    const velocityThreshold = this.config.velocityThreshold;

    // High velocity takes precedence
    if (Math.abs(velocity) > velocityThreshold) {
      if (velocity < -velocityThreshold) {
        this.expandState();
      } else if (velocity > velocityThreshold) {
        this.collapseState();
      }
      return;
    }

    // Distance-based state change
    if (deltaY < -threshold) {
      this.expandState();
    } else if (deltaY > threshold) {
      this.collapseState();
    } else {
      // Snap back to current state
      this.setState(this.currentState);
    }
  }

  /**
   * Expand to next state
   */
  expandState() {
    switch (this.currentState) {
      case STATES.COLLAPSED:
        this.setState(STATES.EXPANDED);
        break;
      case STATES.EXPANDED:
        this.setState(STATES.FULL);
        break;
      case STATES.FULL:
        // Already at max, stay
        break;
    }
  }

  /**
   * Collapse to previous state
   */
  collapseState() {
    switch (this.currentState) {
      case STATES.FULL:
        this.setState(STATES.EXPANDED);
        break;
      case STATES.EXPANDED:
        this.setState(STATES.COLLAPSED);
        break;
      case STATES.COLLAPSED:
        // Already at min, stay
        break;
    }
  }

  /**
   * Toggle between collapsed and expanded
   */
  toggleState() {
    if (this.currentState === STATES.COLLAPSED) {
      this.setState(STATES.EXPANDED);
    } else {
      this.setState(STATES.COLLAPSED);
    }
  }

  /**
   * Set bottom sheet state
   */
  setState(newState, animate = true) {
    if (this.isTransitioning) return;

    const previousState = this.currentState;
    this.currentState = newState;

    if (!animate) {
      addClass(this.container, "bs--no-transition");
    }

    // Remove all state classes
    Object.values(STATE_CLASSES).forEach((className) => {
      removeClass(this.container, className);
    });

    // Add new state class
    if (STATE_CLASSES[newState]) {
      addClass(this.container, STATE_CLASSES[newState]);
    }

    // Update ARIA attributes
    this.updateAccessibility();

    // Reset transform
    this.container.style.transform = "";

    if (animate) {
      this.isTransitioning = true;

      setTimeout(() => {
        this.isTransitioning = false;
        removeClass(this.container, "bs--no-transition");
      }, this.config.animationDuration);
    } else {
      setTimeout(() => {
        removeClass(this.container, "bs--no-transition");
      }, 10);
    }

    // Dispatch state change event
    this.dispatchStateChange(previousState, newState);

    this.log(`State changed: ${previousState} -> ${newState}`);
  }

  /**
   * Update accessibility attributes
   */
  updateAccessibility() {
    const isExpanded = this.currentState !== STATES.COLLAPSED;

    this.container.setAttribute("aria-expanded", isExpanded.toString());
  }

  /**
   * Dispatch state change event
   */
  dispatchStateChange(previousState, newState) {
    const event = new CustomEvent("bottomsheet:statechange", {
      detail: {
        previousState,
        newState,
        container: this.container,
      },
      bubbles: true,
    });

    this.container.dispatchEvent(event);
  }

  /**
   * Public API methods
   */

  getState() {
    return this.currentState;
  }

  expand() {
    this.expandState();
  }

  collapse() {
    this.collapseState();
  }

  toggle() {
    this.toggleState();
  }

  /**
   * Update content dynamically
   */
  updateContent(content) {
    const dynamicContent = this.container.querySelector("[data-bs-dynamic]");
    if (dynamicContent && content) {
      dynamicContent.innerHTML = content;
      this.log("Content updated");
    }
  }

  /**
   * Destroy the bottom sheet instance
   */
  destroy() {
    // Remove Hammer.js instance
    if (this.hammer) {
      this.hammer.destroy();
      this.hammer = null;
    }

    // Remove event listeners
    this.eventListeners.forEach(({ element, event, listener }) => {
      element.removeEventListener(event, listener);
    });
    this.eventListeners = [];

    // Remove classes and styles
    removeClass(this.container, "bottomsheet");
    Object.values(STATE_CLASSES).forEach((className) => {
      removeClass(this.container, className);
    });

    this.container.style.transform = "";

    this.log("Bottom sheet destroyed");
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (this.config.debug) {
      console.log("[BottomSheet]", ...args);
    }
  }
}

// Export for direct usage
export default BottomSheet;
